import { useAppDispatch, useAppSelector } from '~/store/hooks'
import { Airflow } from '~/store/objects/types'
import { useEffect, useMemo } from 'react'
import {
  BufferAttribute,
  Color,
  DataTexture,
  FloatType,
  LinearFilter,
  LinearMipmapLinearFilter,
  RGBAFormat,
  Shape,
  ShapeGeometry,
} from 'three'
import { useParams } from 'react-router-dom'
import { useSuspenseQuery } from '@apollo/client'
import { GET_FACILITY_PRIMARY_USE_QUERY } from '~/client/queries'
import { primaryUseVelocityTypes } from 'config/facility'
import theme from '~/config/theme'
import { modelToUI } from '../util/units'
import { updateAirflow } from '~/lib/airflow/airflow'
import { selectDenormalizedWalls } from '~/store/objects/selectors/walls'
import type { denormalizeWalls } from '~/components/DrawingCanvas/lib/collisionMeshes'

export function AirflowView() {
  const airflow = useAppSelector(state => state.objects.present.airflow)
  const denormalizedWalls = useAppSelector(selectDenormalizedWalls)

  if (airflow.selectedLayer === undefined) {
    return <></>
  }

  const relevantWall = denormalizedWalls.find(wall => wall.id === airflow.selectedLayer?.objectId)
  if (relevantWall === undefined) {
    return <></>
  }

  const { objectId, evaluationHeight } = airflow.selectedLayer
  const currentData = airflow.data?.find(
    airflow => airflow.objectId === objectId && airflow.evaluationHeight === evaluationHeight
  )

  if (currentData === undefined) {
    return <></>
  }

  return <SelectedAirflow airflow={currentData} wall={relevantWall} />
}

function lerpColor(startColor: Color, endColor: Color, percent: number): Color {
  return new Color().lerpColors(startColor, endColor, percent)
}

function getVelocityColor(velocity: number, colors: { color: string; velocity: number }[]) {
  let j = 0
  // If the velocity is lower than the min, set its color to the minimum
  if (colors[j].velocity >= velocity) return new Color(colors[j].color)
  // Traverse the colors array to find the two colors the point is between
  for (let i = 1; i < colors.length; j = i++) {
    if (velocity > colors[j].velocity && velocity < colors[i].velocity) {
      // Find the percentage the velocity is between the two colors
      const percent = (velocity - colors[j].velocity) / (colors[i].velocity - colors[j].velocity)

      // Return the gradient of the two colors
      return lerpColor(new Color(colors[j].color), new Color(colors[i].color), percent)
    } else if (velocity === colors[i].velocity) {
      return new Color(colors[i].color)
    }
  }
  // If we did not return a color, make sure the velocity isn't higher than the maximum
  // If it is, return the color of the maximum
  if (velocity > colors[colors.length - 1].velocity) {
    return new Color(colors[colors.length - 1].color)
  }
  throw new Error('invalid colour')
}

export function useColorScheme() {
  const params = useParams<{ id: string }>()
  const { data } = useSuspenseQuery(GET_FACILITY_PRIMARY_USE_QUERY, {
    variables: {
      id: params.id ?? '',
    },
  })

  let colors = theme.colors.highVelocityAirflow
  if (data.facility?.primaryUse === 'OTHER') {
    const primaryType = data.facility?.primaryType
    if (primaryType === 'RESIDENTIAL' || primaryType === 'COMMERCIAL') {
      colors = theme.colors.lowVelocityAirflow
    }
  } else if (primaryUseVelocityTypes.low.includes(data.facility!.primaryUse)) {
    colors = theme.colors.lowVelocityAirflow
  }

  return colors
}

function useUpdateAirflow() {
  const dispatch = useAppDispatch()
  const products = useAppSelector(state => state.objects.present.products)
  const objects = useAppSelector(state => state.objects.present.objects)
  const segments = useAppSelector(state => state.objects.present.segments)
  const obstructions = useAppSelector(state => state.objects.present.obstructions)

  useEffect(() => {
    updateAirflow(dispatch, { products, objects, segments, obstructions })
  }, [products, objects, segments, obstructions])
}

function SelectedAirflow({ airflow, wall }: { airflow: Airflow; wall: ReturnType<typeof denormalizeWalls>[number] }) {
  useUpdateAirflow()
  const visible = useAppSelector(state => state.layers.layers.AIRFLOW.visible)
  const colorScheme = useColorScheme()
  const colorData = useMemo(() => {
    const velocities = airflow.airflowVelocities

    const rootArrayLength = velocities.length
    const nestedArrayLength = velocities[0].length
    const numVertices = rootArrayLength * nestedArrayLength

    const colors = new Float32Array(numVertices * 4)

    let n = 0

    const addColors = (c: Color) => {
      colors[n++] = c.r
      colors[n++] = c.g
      colors[n++] = c.b
      colors[n++] = 1
    }

    for (let i = 0; i < rootArrayLength; ++i) {
      const velocitiesArray = velocities[i]
      for (let j = 0; j < nestedArrayLength; ++j) {
        const velocity = velocitiesArray[j]
        const color = getVelocityColor(velocity, colorScheme)

        addColors(color)
      }
    }

    return colors
  }, [airflow.airflowVelocities])
  const colorTexture = useMemo(() => {
    const texture = new DataTexture(
      colorData,
      airflow.airflowVelocities[0].length,
      airflow.airflowVelocities.length,
      RGBAFormat,
      FloatType
    )
    texture.magFilter = LinearFilter
    texture.minFilter = LinearMipmapLinearFilter
    texture.needsUpdate = true
    return texture
  }, [colorData, airflow.airflowVelocities])
  useEffect(() => {
    return () => colorTexture.dispose()
  }, [colorTexture])

  const wallGeometry = useMemo(() => {
    const points = wall.segments.map(segment => segment.insetEdge.start)
    points.push(wall.segments[wall.segments.length - 1].insetEdge.end)

    const minX = modelToUI(points.reduce((prev, current) => Math.min(current.x, prev), +Infinity))
    const maxX = modelToUI(points.reduce((prev, current) => Math.max(current.x, prev), -Infinity))
    const minY = modelToUI(points.reduce((prev, current) => Math.min(current.y, prev), +Infinity))
    const maxY = modelToUI(points.reduce((prev, current) => Math.max(current.y, prev), -Infinity))

    const width = maxX - minX
    const height = maxY - minY

    const shape = new Shape()
    shape.moveTo(modelToUI(points[0].x), modelToUI(points[0].y))
    for (const point of points.slice(1, points.length - 1)) {
      shape.lineTo(modelToUI(point.x), modelToUI(point.y))
    }
    const end = wall.segments[wall.segments.length - 1].insetEdge.end
    shape.lineTo(modelToUI(end.x), modelToUI(end.y))
    shape.closePath()

    const geom = new ShapeGeometry(shape)

    const position = geom.attributes.position

    const uvs = new Float32Array(position.count * 2)
    let uvIdx = 0

    for (let i = 0; i < position.count; ++i) {
      const x = position.array[i * 3]
      const y = position.array[i * 3 + 1]

      uvs[uvIdx++] = (x - minX) / width
      uvs[uvIdx++] = (y - minY) / height
    }

    geom.setAttribute('uv', new BufferAttribute(uvs, 2))

    return geom
  }, [wall])
  useEffect(() => {
    return () => wallGeometry.dispose()
  }, [wallGeometry])

  return (
    <mesh geometry={wallGeometry} visible={visible}>
      <meshBasicMaterial map={colorTexture} />
    </mesh>
  )
}
